import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, BookOpen, GraduationCap, Mail, Search, X } from 'lucide-react'
import { filterHelpTopics, helpTopic, type HelpTopicId } from '../help-content'

interface HelpDialogProps {
  open: boolean
  initialTopic?: HelpTopicId
  canResumeTour: boolean
  onClose: () => void
  onStartTour: () => void
}

export function HelpDialog({ open, initialTopic = 'getting-started', canResumeTour, onClose, onStartTour }: HelpDialogProps) {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<HelpTopicId>(initialTopic)
  const matches = useMemo(() => filterHelpTopics(query), [query])
  const selected = helpTopic(selectedId)

  useEffect(() => {
    if (!open) return
    setSelectedId(initialTopic)
    setQuery('')
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [initialTopic, onClose, open])

  if (!open) return null

  return (
    <div className="modal-backdrop help-backdrop" onMouseDown={onClose}>
      <section className="help-dialog" role="dialog" aria-modal="true" aria-labelledby="help-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="help-header">
          <div>
            <span className="modal-kicker">Deckelier support</span>
            <h2 id="help-title">Help & guides</h2>
          </div>
          <button className="icon-button" aria-label="Close help" onClick={onClose}><X size={19} /></button>
        </header>

        <div className="help-search">
          <Search size={17} />
          <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search guides" aria-label="Search help guides" />
          {query && <button aria-label="Clear search" onClick={() => setQuery('')}><X size={15} /></button>}
        </div>

        <div className="help-layout">
          <aside className="help-sidebar">
            <button className="tour-help-card" onClick={onStartTour}>
              <span className="tour-help-icon"><GraduationCap size={20} /></span>
              <span><strong>Build your first deck</strong><small>A short, hands-on introduction</small></span>
              <ArrowRight size={16} />
              <em>{canResumeTour ? 'Resume guided tour' : 'Take the guided tour'}</em>
            </button>

            <nav className="help-topic-list" aria-label="Help topics">
              {matches.map((topic, index) => {
                const previousCategory = matches[index - 1]?.category
                return (
                  <div key={topic.id} className="help-topic-group">
                    {topic.category !== previousCategory && <span>{topic.category}</span>}
                    <button className={topic.id === selected.id ? 'active' : ''} onClick={() => setSelectedId(topic.id)}>
                      <BookOpen size={15} /><span><strong>{topic.title}</strong><small>{topic.summary}</small></span>
                    </button>
                  </div>
                )
              })}
              {!matches.length && <div className="help-no-results"><strong>No matching guide</strong><span>Try a shorter search.</span></div>}
            </nav>

            <a className="help-feedback" href="mailto:feedback@deckelier.com?subject=Deckelier%20feedback">
              <Mail size={17} /><span><strong>Send feedback</strong><small>feedback@deckelier.com</small></span>
            </a>
          </aside>

          <article className="help-article">
            <span className="eyebrow plain">{selected.category}</span>
            <h3>{selected.title}</h3>
            <p className="help-summary">{selected.summary}</p>
            <ol>
              {selected.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
            {selected.tip && <aside className="help-tip"><strong>Good to know</strong><p>{selected.tip}</p></aside>}
          </article>
        </div>
      </section>
    </div>
  )
}
